import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const SpendingPatternChart = ({ data, chartType }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-semibold text-foreground font-data">
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Spending Pattern Analysis</h3>
        <div className="flex items-center gap-2">
          <Icon name="TrendingUp" size={16} className="text-success" />
          <span className="text-sm text-success font-semibold">+18.5% MoM</span>
        </div>
      </div>

      <div className="w-full h-80 md:h-96" aria-label="Spending Pattern Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value / 1000}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Bar
                dataKey="proUsers"
                fill="var(--color-primary)"
                name="Pro Users"
                radius={[8, 8, 0, 0]}
              />
              <Bar
                dataKey="freeUsers"
                fill="var(--color-secondary)"
                name="Free Users"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          ) : (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value / 1000}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Line
                type="monotone"
                dataKey="proUsers"
                stroke="var(--color-primary)"
                strokeWidth={3}
                name="Pro Users"
                dot={{ fill: 'var(--color-primary)', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="freeUsers"
                stroke="var(--color-secondary)"
                strokeWidth={3}
                name="Free Users"
                dot={{ fill: 'var(--color-secondary)', r: 4 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-muted/20 rounded-lg">
          <div className="text-xs text-muted-foreground caption mb-1">Avg. Pro Spend</div>
          <div className="text-xl font-bold text-foreground font-data">$847</div>
          <div className="text-xs text-success mt-1">+12.3%</div>
        </div>
        <div className="p-4 bg-muted/20 rounded-lg">
          <div className="text-xs text-muted-foreground caption mb-1">Avg. Free Spend</div>
          <div className="text-xl font-bold text-foreground font-data">$124</div>
          <div className="text-xs text-success mt-1">+8.7%</div>
        </div>
        <div className="p-4 bg-muted/20 rounded-lg">
          <div className="text-xs text-muted-foreground caption mb-1">Total Volume</div>
          <div className="text-xl font-bold text-foreground font-data">$2.4M</div>
          <div className="text-xs text-success mt-1">+18.5%</div>
        </div>
        <div className="p-4 bg-muted/20 rounded-lg">
          <div className="text-xs text-muted-foreground caption mb-1">Transactions</div>
          <div className="text-xl font-bold text-foreground font-data">8,432</div>
          <div className="text-xs text-success mt-1">+15.2%</div>
        </div>
      </div>
    </div>
  );
};

export default SpendingPatternChart;